import { useState } from 'react';
import { CheckCircle2, Phone, ArrowRight, Shield, Clock, Award, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PricingCard } from '../components/PricingCard';
import { RewireQuoteCalculator } from '../components/RewireQuoteCalculator';
import { SEO } from '../components/SEO';
import { companyInfo } from '../data/pricing';

export const Rewiring = () => {
  const [showCalculator, setShowCalculator] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <SEO 
        title="House & Flat Rewiring London - From £4,950 | Crescent Electrical"
        description="Professional property rewiring across London. Full & partial rewires from £4,950. NICEIC approved contractors. Fixed price quotes. Get your rewire estimate today."
        canonical="https://crescentelectrical.co.uk/rewiring"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0B132B] to-[#1C2541] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block mb-4">
              <span className="text-[#0066FF] uppercase tracking-[0.2em] text-xs font-bold">
                FROM £4,950
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6">
              HOUSE & FLAT REWIRING
              <span className="block text-[#0066FF] mt-2">ACROSS LONDON</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Complete property rewiring with NICEIC certification. Fixed price quotes for 1–5+ bedroom properties. 
              Occupied or vacant.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button 
                size="lg"
                onClick={() => window.location.href = '/rewire-quote'}
                className="bg-[#0066FF] hover:bg-[#0052CC] text-white text-lg px-8"
              >
                <Zap className="mr-2 h-5 w-5" />
                Get My Rewire Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => setShowCalculator(true)}
                className="border-2 border-white text-white hover:bg-white hover:text-[#0B132B] text-lg px-8"
              >
                Calculate My Price
              </Button>
            </div>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                <Shield className="h-5 w-5 text-[#0066FF]" />
                <span className="text-sm font-semibold">NICEIC Approved</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                <Award className="h-5 w-5 text-[#F59E0B]" />
                <span className="text-sm font-semibold">Fully Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                <Clock className="h-5 w-5 text-[#F59E0B]" />
                <span className="text-sm font-semibold">Fixed Timescales</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
              Transparent Fixed Pricing
            </h2>
            <p className="text-lg text-[#475569] max-w-3xl mx-auto">
              All prices include complete rewire, new consumer unit, testing and NICEIC certification
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <PricingCard
              tier="1–2 BED"
              price="FROM £4,950"
              duration="3–5 Days"
              features={[
                "100% New Circuit Wiring",
                "Dual RCD Consumer Unit",
                "First & Second Fix",
                "NICEIC Certification"
              ]}
              onCTAClick={() => window.location.href = '/rewire-quote'}
            />
            <PricingCard
              tier="3 BED HOUSE"
              price="£6,050 – £7,150"
              duration="5–7 Days"
              features={[
                "Complete Circuit Overhaul",
                "Surge Protected Board",
                "Kitchen & Bathroom Zones",
                "Full Test & Certification"
              ]}
              highlighted={true}
              onCTAClick={() => window.location.href = '/rewire-quote'}
            />
            <PricingCard
              tier="4 BED HOUSE"
              price="£7,150 – £7,700"
              duration="7–10 Days"
              features={[
                "Heavy Load Capacity",
                "EV & Solar Ready Board",
                "LED Layout Planning",
                "Building Control Sign-off"
              ]}
              onCTAClick={() => window.location.href = '/rewire-quote'}
            />
            <PricingCard
              tier="5 BED HOUSE"
              price="£8,250 – £9,350+"
              duration="10–14 Days"
              features={[
                "3-Phase Distribution",
                "Smart Automation Prep",
                "Bespoke Lighting Circuits",
                "Dedicated Site Manager"
              ]}
              onCTAClick={() => window.location.href = '/rewire-quote'}
            />
          </div>

          <div className="mt-12 bg-[#FFF7ED] border-2 border-[#F59E0B]/30 rounded-xl p-6 text-center">
            <p className="text-[#0F172A]">
              <strong className="text-[#D97706]">Occupied Properties:</strong> Additional £400–£800 for furniture protection and access coordination
            </p>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold text-[#0F172A] mb-8">
                Complete Rewiring Package
              </h2>
              <div className="space-y-4">
                {[
                  'Full property survey and quotation',
                  'Complete removal of old wiring',
                  '100% brand new circuit installation',
                  'New consumer unit (fuseboard)',
                  'Sockets, switches and light fittings',
                  'Kitchen and bathroom electrical zones',
                  'Smoke and heat alarm installation',
                  'Full electrical testing',
                  'NICEIC Electrical Installation Certificate',
                  'Building Control notification',
                  '6-year NICEIC warranty'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg border border-gray-200">
                    <CheckCircle2 className="h-6 w-6 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span className="text-[#0F172A] font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-4xl font-bold text-[#0F172A] mb-8">
                Why Rewire Your Property?
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Safety',
                    desc: 'Old wiring can be dangerous. Modern rewiring eliminates fire and shock risks.'
                  },
                  {
                    title: 'Insurance Requirements',
                    desc: 'Many insurers require properties over 30 years old to have updated wiring.'
                  },
                  {
                    title: 'Property Sale',
                    desc: 'Buyers and mortgage lenders expect safe, certified electrical installations.'
                  },
                  {
                    title: 'Modern Demands',
                    desc: 'Old wiring cannot handle modern electrical loads (appliances, EV charging, etc.)'
                  },
                  {
                    title: 'Renovation Opportunity',
                    desc: 'Add extra sockets, USB points, smart home wiring and improved lighting.'
                  }
                ].map((item, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-xl border-2 border-gray-200">
                    <h3 className="text-xl font-bold text-[#0F172A] mb-2">{item.title}</h3>
                    <p className="text-[#475569]">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      {showCalculator && (
        <section className="py-20 bg-white" id="calculator">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0F172A] mb-4">
                Calculate Your Rewire Cost
              </h2>
              <p className="text-lg text-[#475569]">
                Get an instant estimate based on your property details
              </p>
            </div>
            <RewireQuoteCalculator />
          </div>
        </section>
      )}

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-4xl font-bold text-[#0F172A] text-center mb-12">
            How Our Rewiring Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { num: '01', title: 'SURVEY', desc: 'Free site visit and detailed quotation' },
              { num: '02', title: 'FIRST FIX', desc: 'Cable installation before plastering' },
              { num: '03', title: 'SECOND FIX', desc: 'Accessories, sockets and switches' },
              { num: '04', title: 'TEST & CERTIFY', desc: 'Full testing and NICEIC certification' }
            ].map((step) => (
              <div key={step.num} className="bg-[#F8F9FA] rounded-xl p-6 border-2 border-gray-200 text-center">
                <div className="w-12 h-12 bg-[#0066FF] text-white rounded-xl flex items-center justify-center font-mono font-bold mx-auto mb-4">
                  {step.num}
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{step.title}</h3>
                <p className="text-sm text-[#475569]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0B132B] to-[#1C2541] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Rewire Your Property?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Get your fixed price quote today. Free site surveys across London.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button 
              size="lg"
              onClick={() => window.location.href = '/rewire-quote'}
              className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
            >
              Get My Rewire Quote
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => setShowCalculator(true)}
              className="border-2 border-white text-white hover:bg-white hover:text-[#0B132B]"
            >
              Calculate My Price
            </Button>
          </div>
          <a 
            href={`tel:${companyInfo.phone}`}
            className="inline-flex items-center gap-2 text-[#0066FF] hover:text-[#F59E0B] font-semibold text-lg"
          >
            <Phone className="h-5 w-5" />
            {companyInfo.phone}
          </a>
        </div>
      </section>
    </div>
  );
};
